import { BehaviorSubject, Observable, Subject, finalize, takeUntil } from 'rxjs';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { KendoGridService, StateFilters } from '@app/core/services/custom-services/kendo-grid.service';
import { CreateGlulamSpecForm } from '@app/core/models/forms/gluelam/glulam-spec/create-glulam-spec-form.model';
import { EntityFormComponent } from '@app/features/model/entity-admin/entity-form/entity-form.component';
import { ActivityFormComponent } from '@app/features/model/entity-admin/activity-form/activity-form.component';
import { FormGroup } from '@angular/forms';
import { GlulamSpecComponent } from '@app/features/gluelam/controls/glulam-spec/glulam-spec.component';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { Guid } from 'guid-typescript';
import { IEntityGroup } from '../../custom-services/entity-group.service';
import { ISawPatternDTO } from '@app/core/models/sawPatternDTO.model';
import { ISawPatternHeaderDTO } from '@app/core/models/saw-pattern-header.model';
import { IUnitConversion } from '@app/core/models/unitConversion.model';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { TranslateService, _ } from '@ngx-translate/core';
import { environment } from '@environment/environment';
import { EntityBulkEditComponent } from '@app/features/model/entity-admin/entity-bulk-edit/entity-bulk-edit.component';
import { EntityStatusEnum } from '@app/core/models/entity-status.enum';
import { EntityMultiselectComponent } from '@app/features/model/entity-admin/entity-multiselect/entity-multiselect.component';
import { EntityPropertyDataTypeEnum } from '@app/core/models/entity-property-data-type.enum';

_('EntityTranslation.NewResource');
_('EntityTranslation.NewActivity');
_('EntityTranslation.UpdateResource');
_('EntityTranslation.UpdateActivity');

@Injectable({
  providedIn: 'root',
})
export class EntityService extends BehaviorSubject<GridDataResult> implements OnDestroy {
  loading: boolean = false;
  private _entityGroupIndex: number;
  private readonly destroy$ = new Subject<void>();
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);
  private readonly kendoGridService = inject(KendoGridService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    super(null);
  }

  queryGrid(state: State) {
    const filter = this.kendoGridService.getStateFilter(state);
    filter.id = this._entityGroupIndex;
    const changeDateFilter = filter.filter.find(f => f.code === 'changeDate');

    if (changeDateFilter?.value) {
      changeDateFilter.value = new Date(changeDateFilter.value).toISOString();
    }

    this.loading = true;
    this.http
      .post<GridDataResult>(`${environment.apiUrl}model/entity/query`, JSON.stringify(filter), {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe(r => super.next(r));
  }

  listMaterials(state: State, budgetId: Guid, budgetItemId: Guid, isSalesGroup: boolean = false): Observable<GridDataResult> {
    let params = new HttpParams().set('skip', state.skip).set('take', state.take).set('isSalesGroup', isSalesGroup);

    const materialDescription: any = state.filter.filters.find(x => x['field'] === 'materialDescription');
    if (materialDescription) params = params.set('materialDescription', materialDescription.value);

    const entityGroupIndex: any = state.filter.filters.find(x => x['field'] === 'entityGroupIndex');
    if (entityGroupIndex) params = params.set('entityGroupIndex', entityGroupIndex.value);

    const isMaterialUsed: any = state.filter.filters.find(x => x['field'] === 'isMaterialUsed');
    if (isMaterialUsed) params = params.set('isMaterialUsed', isMaterialUsed.value);

    if (budgetId) params = params.set('budgetId', String(budgetId));

    if (budgetItemId) params = params.set('budgetItemId', budgetItemId.toString());

    return this.http.get<GridDataResult>(`${environment.apiUrl}model/entity`, { params });
  }

  update(entity: IEntity): Observable<any> {
    return this.http.put<IEntity>(`${environment.apiUrl}model/entity`, entity);
  }

  insert(entity: IEntity): Observable<IEntity> {
    return this.http.post<IEntity>(`${environment.apiUrl}model/entity`, entity);
  }

  openActivityDialog(entity: IEntity, entityGroup: IEntityGroup, isNew: boolean): DialogRef {
    const title = isNew ? this.translateService.instant('EntityTranslation.NewActivity') : this.translateService.instant('EntityTranslation.UpdateActivity');
    const dialogRef = this.dialogService.open({
      content: ActivityFormComponent,
      title: title,
      width: '1900px',
      maxHeight: '95%',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as ActivityFormComponent;
    content.data = { entity, entityGroup, isNew };

    return dialogRef;
  }

  openResourceDialog(entity: IEntity, entityGroup: IEntityGroup, isNew: boolean): DialogRef {
    const title = isNew ? this.translateService.instant('EntityTranslation.NewResource') : this.translateService.instant('EntityTranslation.UpdateResource');
    const dialogRef = this.dialogService.open({
      content: EntityFormComponent,
      title: title,
      width: '1900px',
      maxHeight: '95%',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as EntityFormComponent;
    content.data = { entity, entityGroup, isNew, entityTypeCode: 'RESOURCE' };

    return dialogRef;
  }

  openEntityMultiSelectDialog(budgetId: Guid = null, budgetItemId: Guid = null, showMarket: boolean = false): DialogRef {
    let title = this.translateService.instant('EntityTranslation.SelectMaterials');

    const dialogRef = this.dialogService.open({
      content: EntityMultiselectComponent,
      title: title,
      width: '1650px',
      maxHeight: '90%',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as EntityMultiselectComponent;
    content.data = { showMarket, budgetId, budgetItemId };

    return dialogRef;
  }

  setSelectedGroup(entityGroupCode: number) {
    this._entityGroupIndex = entityGroupCode;
  }

  get(entityIndex: number, dontExpandPropertys: boolean = false): Observable<IEntity> {
    return this.http.get<IEntity>(`${environment.apiUrl}model/entity/${entityIndex}`, {
      params: new HttpParams().set('dontExpandPropertys', dontExpandPropertys),
    });
  }

  getSummary(guid: Guid): Observable<IEntitySummaryDTO> {
    return this.http.get<IEntitySummaryDTO>(`${environment.apiUrl}model/entity/summary/` + guid.toString());
  }

  searchEntities(entityTypeCode: string, code: string, take: number, getMaterialStandardProperties: boolean = false): Observable<IEntity[]> {
    return this.http.get<IEntity[]>(`${environment.apiUrl}model/entity/search`, {
      params: new HttpParams().set('entityTypeCode', entityTypeCode).set('code', code).set('take', take.toString()).set('getMaterialStandardProperties', getMaterialStandardProperties),
    });
  }

  searchEntitiesV2(entityTypeCode: string, code: string, inOperative: boolean, take: number): Observable<IEntitySearchResultDTO[]> {
    return this.http.get<IEntitySearchResultDTO[]>(`${environment.apiUrl}model/entity/searchEntitiesV2`, {
      params: new HttpParams().set('entityTypeCode', entityTypeCode).set('code', code).set('inOperative', inOperative).set('take', take.toString()),
    });
  }

  getEntitiesThatProduce(productIndex: number, direction: number): Observable<IEntity[]> {
    return this.http.get<IEntity[]>(`${environment.apiUrl}model/entity/relations/${productIndex}`, {
      params: new HttpParams().set('direction', direction.toString()),
    });
  }

  openDialogForGlulamSpec(entity: IEntity, form: FormGroup<CreateGlulamSpecForm>): DialogRef {
    const dialogRef = this.dialogService.open({
      content: GlulamSpecComponent,
      width: '1000px',
      height: '600px',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as GlulamSpecComponent;

    content.setup(entity, form);

    return dialogRef;
  }

  getPattern(index: number) {
    return this.http.get<ISawPatternDTO>(`${environment.apiUrl}operative/saw-pattern/${index}`);
  }

  getSawPatternTable(entityIndex: number) {
    return this.http.get<ISawPatternHeaderDTO>(`${environment.apiUrl}model/saw-pattern`, { params: { entityIndex } });
  }

  bulkUpdate(entity: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}model/entity/bulk-update`, entity);
  }

  calcGluSpec(index: number, planingThickness: number, height: number, isBeamMaterialHight: boolean, useDimensionalAccuracy: boolean): Observable<IGlulamSpecCalcResult> {
    let params = new HttpParams();

    params = params.append('planingThickness', planingThickness);
    params = params.append('height', height);
    params = params.append('index', index);
    params = params.append('isBeamMaterialHight', isBeamMaterialHight);
    params = params.append('useDimensionalAccuracy', useDimensionalAccuracy);

    return this.http.get<IGlulamSpecCalcResult>(`${environment.apiUrl}GluSpec`, { params });
  }

  openBatchEditDialog(entityIndexes: number[], entityGroupIndex: number | null = null): DialogRef {
    const dialogRef = this.dialogService.open({
      content: EntityBulkEditComponent,
      width: 1000,
      height: 'auto',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as EntityBulkEditComponent;
    content.setupFrom(entityIndexes, entityGroupIndex);

    return dialogRef;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export interface IEntity {
  baseUnitCode: string;
  changeDate: string | null;
  code: string;
  commentId: number | null;
  description: string;
  entityGroupCode: string;
  index: number;
  isDynamicActivity?: boolean | null;
  sortOrderIndex: number | null;
  status: number | null;
  superiorEntityIndex?: number | null;
  userCode: string;
  machineId: number | null;
  propertyValues: IPropertyValue[];
  entityRelations: IEntityRelation[];
  unitConversions: IUnitConversion[];
  entityGroup?: IEntityGroup;
  targetMinStock: number | null;
  targetMaxStock: number | null;
  batchTime: number | null;
  batchSize: number | null;
  guid: string;
  gluSpecHightMin?: number | null;
  gluSpecHightMax?: number | null;
  gluSpecTopPercentage?: number | null;
  gluSpecBottomPercentage?: number | null;
  gluSpecAddedCalcHight?: number | null;
  gluSpecAddedCalcWidth?: number | null;
  standardProperties?: IStandardProperties;
  beamLamminaDimensionId: number | null;
  autoGenerateProdOrder: boolean;
  instruction: string;
  isConnectedToMaterialBudget: boolean;
}

export interface IEntitySearchResultDTO {
  code: string;
  description: string;
  entityGroupCode: string;
  index: number;
  siteCode: string;
  siteId: Guid;
  entityGroupDescription?: string;
}

export interface IPropertyValue {
  id: number | null;
  description: string | null;
  entityIndex: number;
  propertyCode: string;
  valueInt: number | null;
  valueLongInt: number | null;
  valueDouble: number | null;
  valueString: string;
  userCode: string;
  changeDate: string | null;
  dataType: EntityPropertyDataTypeEnum;
  isMandatory: boolean;
  areValuesValidated: boolean;
}
export interface IEntityRelation {
  sortOrder: number;
  productIndex: number;
  activityIndex: number;
  relation: number;
  direction: EntityRelationDirection;
  unitCode: string;
  userCode: string;
  ChangeDate: string | null;

  productCode: string;
  activityCode: string;
  isNew: boolean;
  isChange: boolean;
  isRemoved: boolean;
  isUnlockQtyRelation: boolean;
  isUnlockUOMRelation: boolean;

  inTactical: boolean;
  inOperative: boolean;
  isChaining: boolean;
  product: IEntity;
  activity: IEntity;
  plannedQty: number;
  reportedQty: number;
  glulamSpec: IGlulamSpec[];
  forecastVolume: number;
  isPreferred: boolean;
  batchTemplateId: Guid | null;
}

export interface IGlulamSpecCalcResult {
  lams: IGlulamSpec[];
  numberOfLaminas: number;
  calculatedPercentageOuterTop: number | null;
  calculatedPercentageOuterBottom: number | null;
}

export enum EntityRelationDirection {
  PRODUCE = 0,
  CONSUMES = 1,
}

export interface DemandStateFilter extends StateFilters {
  showPlanned: boolean;
  showOnlyWithNoGluPlan: boolean;
}

export interface EntityFilter extends StateFilters {
  siteId: Guid;
  rawLamellaThickness: number;
  beamMaterialExternalId: number;
  entityGroupCode: string;
}

export interface IGlulamSpec {
  activityIndex: number;
  productIndex: number;
  sequence: number;
  numberOfLammellas: number;
  numberOfLammellasCalc?: number;
  planingThickness: number;
  heightDependant: boolean;
  dryJoint: boolean;
  turn: boolean;
  isNew: boolean;
  isChanged: boolean;
  isRemoved: boolean;
  layerType: number;
}

export interface IStandardProperties {
  length: number | null;
  thickness: number | null;
  width: number | null;
  species: string;
  strengthClass: string;
  visualQuality: string;
}

export interface IEntitySummaryDTO {
  entityNumber: number;
  entityId: Guid;
  externalId: string;
  code: string;
  description: string;
  changeDate: Date;
  entityGroupNumber: number;
  status: EntityStatusEnum;
  typeCode: string;
  materials: IEntityGroupSummaryDTO[];
  activities: IEntityGroupSummaryDTO[];
}

export interface IEntityGroupSummaryDTO {
  entityNumber: number;
  entityId: Guid;
  entityCode: string;
  entityDescription: string;
  changeDate: Date;
  status: EntityStatusEnum;
}

export interface IEntityGridItem {
  index: number;
  code: string;
  description: string;
  status: number | null;
  statusText: string;
  changeDate: string | null;
  entityGroup: IEntityGroupGrid;
  propertyValues: IPropertyValueGrid[];
}

export interface IEntityGroupGrid {
  description: string;
  isSawingPattern: boolean | null;
}

export interface IPropertyValueGrid {
  propertyCode: string;
  valueInt: number | null;
  valueDouble: number | null;
  valueString: string;
  dataType: number;
}
